import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_Variables() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">
        🎨 Advanced CSS: Variables (Custom Properties)
      </h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS Variables (Custom Properties) allow you to define reusable values
        and dynamically control colors, spacing, fonts, animations, and more.
        Advanced usage includes theming, component-level variables, responsive
        values, and animation control.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg,#e0f7fa,#b2ebf2)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#006064" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Define global variables under <code>:root</code> for site-wide
            usage.
          </li>
          <li>Use component-level variables for isolated styling.</li>
          <li>
            Variables can control colors, font sizes, spacing, shadows,
            gradients, and more.
          </li>
          <li>
            Use <code>var(--variable-name)</code> to reference them anywhere in
            CSS.
          </li>
          <li>
            Combine with media queries and JavaScript for dynamic theming.
          </li>
        </ul>
      </div>

      <div className="tutorial-section">
        <h2 className="section-title">📝 Examples of Advanced CSS Variables</h2>

        {/* 1. Global Theming */}
        <h3>1. Global Light/Dark Theme</h3>
        <CodeCopyBlock
          code={`:root {
  --primary-color: #3b82f6;
  --secondary-color: #9333ea;
  --background-color: #fef3c7;
  --text-color: #1f2937;
}

[data-theme='dark'] {
  --primary-color: #10b981;
  --secondary-color: #06b6d4;
  --background-color: #111827;
  --text-color: #f3f4f6;
}

body {
  background: var(--background-color);
  color: var(--text-color);
}`}
        />
        <div
          style={{
            background: "var(--background-color,#fef3c7)",
            color: "var(--text-color,#1f2937)",
            padding: "20px",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        >
          <p>
            This container adapts to light or dark theme using CSS variables.
          </p>
        </div>

        {/* 2. Component-level Variables */}
        <h3>2. Component Scoped Variables</h3>
        <CodeCopyBlock
          code={`.card {
  --card-bg: #fff;
  --card-shadow: 0 8px 20px rgba(0,0,0,0.1);
  background: var(--card-bg);
  box-shadow: var(--card-shadow);
  padding: 20px;
  border-radius: 12px;
}`}
        />
        <div
          style={{
            "--card-bg": "#dbeafe",
            "--card-shadow": "0 12px 25px rgba(59,130,246,0.2)",
            background: "var(--card-bg)",
            boxShadow: "var(--card-shadow)",
            padding: "20px",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        >
          <p>Card with scoped variables for background and shadow.</p>
        </div>

        {/* 3. Variables in Gradients and Animations */}
        <h3>3. Gradients & Animations with Variables</h3>
        <CodeCopyBlock
          code={`:root {
  --gradient-start: #3b82f6;
  --gradient-end: #9333ea;
  --animation-speed: 3s;
}

.button {
  background: linear-gradient(90deg,var(--gradient-start),var(--gradient-end));
  padding: 10px 20px;
  border-radius: 8px;
  color: white;
  animation: slide var(--animation-speed) infinite alternate;
}

@keyframes slide {
  0% { background-position: 0%; }
  100% { background-position: 100%; }
}`}
        />
        <div
          style={{
            background:
              "linear-gradient(90deg,var(--gradient-start,#3b82f6),var(--gradient-end,#9333ea))",
            padding: "10px 20px",
            borderRadius: "8px",
            color: "white",
            display: "inline-block",
            backgroundSize: "200% 200%",
            animation: "slide 3s infinite alternate",
            marginBottom: "1rem",
          }}
        >
          Gradient Button
        </div>

        {/* 4. Responsive Variables */}
        <h3>4. Responsive Values Using Variables</h3>
        <CodeCopyBlock
          code={`:root {
  --column-count: 1;
}

@media(min-width:768px) {
  :root { --column-count: 2; }
}

@media(min-width:1024px) {
  :root { --column-count: 3; }
}

.container {
  column-count: var(--column-count);
  column-gap: 20px;
}`}
        />
        <div
          style={{
            columnCount: "var(--column-count,1)",
            columnGap: "20px",
            background: "#fef3c7",
            padding: "15px",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        >
          <p>Responsive Column 1</p>
          <p>Responsive Column 2</p>
          <p>Responsive Column 3</p>
          <p>Responsive Column 4</p>
        </div>

        {/* 5. Dynamic Color Switcher */}
        <h3>5. Dynamic Theme Switcher with JavaScript</h3>
        <CodeCopyBlock
          code={`<button onclick="document.documentElement.setAttribute('data-theme','dark')">Dark</button>
<button onclick="document.documentElement.setAttribute('data-theme','light')">Light</button>`}
        />
        <div style={{ display: "flex", gap: "10px", marginBottom: "2rem" }}>
          <button
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              cursor: "pointer",
              border: "none",
            }}
            onClick={() =>
              document.documentElement.setAttribute("data-theme", "dark")
            }
          >
            Dark Theme
          </button>
          <button
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              cursor: "pointer",
              border: "none",
            }}
            onClick={() =>
              document.documentElement.setAttribute("data-theme", "light")
            }
          >
            Light Theme
          </button>
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg,#e0f7fa,#b2ebf2)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#006064" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Experiment with <strong>CSS variables + transitions</strong> for
            animated themes.
          </li>
          <li>
            Combine variables with{" "}
            <strong>gradients, shadows, and hover effects</strong> for component
            libraries.
          </li>
          <li>
            Use <strong>JavaScript</strong> to dynamically update variables for
            live theming.
          </li>
          <li>
            Scope variables at component level for isolated reusable UI
            elements.
          </li>
        </ul>
      </div>
    </div>
  );
}
